import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  signal,
  contentChildren,
  effect,
  input,
  output
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ToggleButtonComponent } from './components/toggle-button.component';

@Component({
  selector: 'zui-toggle-button-group',
  templateUrl: './toggle-button-group.component.html',
  styleUrl: './toggle-button-group.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleButtonGroupComponent),
      multi: true
    }
  ]
})
export class ToggleButtonGroupComponent implements ControlValueAccessor {
  // # Content projection
  buttons = contentChildren(ToggleButtonComponent);

  // # Inputs
  // Controlled mode: when bound, the group reflects this instead of whatever
  // writeValue last supplied — for selection that is derived rather than stored.
  value = input<any>(undefined);

  // # Outputs
  valueChange = output<any>();

  // # Control Value Accessor
  valueRef = signal<any>(null);

  constructor() {
    // Watch for buttons changes and update states when they appear
    effect(() => {
      const buttons = this.buttons();
      const controlled = this.value();

      if (controlled !== undefined) {
        this.valueRef.set(controlled);
      } else {
        this.valueRef();
      }

      if (buttons.length > 0) {
        this.#updateButtonStates();
      }
    }, { allowSignalWrites: true });
  }

  writeValue(v: any) {
    this.valueRef.set(v);
    this.#updateButtonStates();
  }

  registerOnChange(fn: any) {
    this._propagateChange = fn;
  }

  registerOnTouched(_: any) {
    return;
  }

  private _propagateChange = (_: any) => {
    return;
  };

  // # Methods
  selectValue(value: any) {
    this.valueRef.set(value);
    this._propagateChange(value);
    this.valueChange.emit(value);
    this.#updateButtonStates();
  }

  #updateButtonStates() {
    const currentValue = this.valueRef();
    this.buttons().forEach(button => {
      const isActive = button.value() === currentValue;
      button.setActive(isActive);
    });
  }
}
